import { createClient, createConfig } from "@/generated/api/client"
import type { Client, ResolvedRequestOptions } from "@/generated/api/client"
import { withDeadline } from "@/platform/api/deadline"
import { apiErrorFromBody } from "@/platform/api/errors"
import type { CacheIntent } from "@/platform/api/identity"
import type { ForwardedSession, RequestCredentialKind } from "@/platform/api/session-contract"
import { attachRequestContext } from "@/platform/observability/errors"
import { ANONYMOUS_VIEWER } from "@/platform/session/viewer"

const TIMEOUT_MS = 20_000
const PEER_SERVICE = "api-v2"
const EXPECTED_USER_HEADER = "X-Nelo-Expected-User-UUID"
const SESSION_PATH_PREFIX = "/oauth2/web/"

export interface NextFetchOptions {
  tags?: string[]
  revalidate?: number | false
}

export interface RequestPolicy {
  credentials?: RequestCredentialKind
  intent?: CacheIntent
  cache?: RequestCache
  next?: NextFetchOptions
  signal?: AbortSignal
  timeoutMs?: number
  headers?: Record<string, string>
  setsSession?: boolean
}

export interface ApiConfig {
  baseUrl: string
  credentials?: RequestCredentialKind
  viewer?: string
  forwardedSession?: ForwardedSession
  resolveHeaders?: (intent: CacheIntent) => Record<string, string>
  onSessionIdentityChanged?: (viewer: string) => void
  onUnauthorized?: (viewer: string) => void
}

export interface CallOptions {
  client: Client
  throwOnError: true
  meta: { policy: RequestPolicy }
  signal?: AbortSignal
  headers?: Record<string, string>
}

export interface Api {
  readonly client: Client
  readonly viewer: string
  request: (policy?: RequestPolicy) => CallOptions
}

interface FetchExtensions {
  signal: AbortSignal
  next?: NextFetchOptions
  cache?: RequestCache
  credentials?: RequestCredentials
  opentelemetry: {
    spanName: string
    attributes: Record<string, string>
  }
}

const policyOf = (options: ResolvedRequestOptions): RequestPolicy => (options as { meta?: { policy?: RequestPolicy } }).meta?.policy ?? {}

const isIdentityChange = async (response: Response): Promise<boolean> => {
  const payload: unknown = await response
    .clone()
    .json()
    .catch(() => null)
  return !!payload && typeof payload === "object" && "error" in payload && payload.error === "session_identity_changed"
}

export const createApi = (config: ApiConfig): Api => {
  const defaultCredentials = config.credentials ?? "public"
  const viewer = config.viewer ?? ANONYMOUS_VIEWER
  const extensions = new WeakMap<Request, FetchExtensions>()
  const attempts = new WeakMap<Request, { request_operation: string; credential_kind: string; viewer: string; started_at: number }>()
  const client = createClient(createConfig({ baseUrl: config.baseUrl }))

  const credentialsOf = (policy: RequestPolicy): RequestCredentialKind => policy.credentials ?? defaultCredentials

  client.interceptors.request.use((request, options) => {
    const policy = policyOf(options)
    const kind = credentialsOf(policy)
    const pathname = new URL(request.url).pathname
    const privateCookieRequest = kind === "cookie-session" && !pathname.startsWith(SESSION_PATH_PREFIX)
    const forwarded = kind === "cookie-session" ? config.forwardedSession : undefined

    const headers = new Headers(request.headers)
    for (const [name, value] of Object.entries(config.resolveHeaders?.(policy.intent ?? "dynamic") ?? {})) {
      if (!headers.has(name)) headers.set(name, value)
    }
    headers.delete(EXPECTED_USER_HEADER)
    if (privateCookieRequest) headers.set(EXPECTED_USER_HEADER, forwarded ? (forwarded.userUuid ?? ANONYMOUS_VIEWER) : viewer)
    if (kind === "cookie-session" || kind === "checkout-token") {
      headers.delete("authorization")
      headers.delete("cookie")
    }
    if (forwarded) {
      headers.set("cookie", forwarded.cookie)
      headers.set("origin", forwarded.origin)
    }

    const withCookies = kind === "cookie-session" || policy.setsSession === true
    const noStore = withCookies
    const signal = withDeadline(policy.timeoutMs ?? TIMEOUT_MS, request.signal)
    const credentials = withCookies && !forwarded ? ("include" as const) : undefined
    const prepared = new Request(request, { headers, signal, ...(credentials ? { credentials } : {}) })

    attempts.set(prepared, {
      request_operation: `${prepared.method} ${options.url.split("?")[0]}`,
      credential_kind: kind,
      viewer,
      started_at: performance.now(),
    })
    const spanName = `${prepared.method} ${options.url.split("?")[0]}`
    const cache = noStore ? "no-store" : policy.cache
    const next = noStore ? undefined : policy.next
    extensions.set(prepared, {
      signal,
      ...(next ? { next } : {}),
      ...(cache ? { cache } : {}),
      ...(credentials ? { credentials } : {}),
      opentelemetry: { spanName, attributes: { "peer.service": PEER_SERVICE, "resource.name": spanName } },
    })
    return prepared
  })

  client.interceptors.response.use(async (response, request, options) => {
    const policy = policyOf(options)
    const kind = credentialsOf(policy)
    if (kind === "cookie-session" && response.status === 409 && (await isIdentityChange(response))) config.onSessionIdentityChanged?.(viewer)
    if (kind === "cookie-session" && response.status === 401 && !new URL(request.url).pathname.startsWith(SESSION_PATH_PREFIX)) config.onUnauthorized?.(viewer)
    return response
  })

  client.interceptors.error.use((error, response, request) => {
    const failure = response && !response.ok ? apiErrorFromBody(response.status, error) : error
    const attempt = request ? attempts.get(request) : undefined
    if (attempt) {
      const { started_at, ...context } = attempt
      attachRequestContext(failure, { ...context, duration_ms: Math.max(0, performance.now() - started_at), ...(response ? { http_status: response.status } : {}) })
    }
    return failure
  })

  client.setConfig({
    fetch: ((input: Request) => fetch(input, extensions.get(input) as RequestInit | undefined)) as typeof fetch,
  })

  return {
    client,
    viewer,
    request: (policy = {}) => ({
      client,
      throwOnError: true,
      meta: { policy },
      ...(policy.signal ? { signal: policy.signal } : {}),
      ...(policy.headers ? { headers: policy.headers } : {}),
    }),
  }
}
